import { RuleTableCell } from "./Structure/RuleTableCell";
import { RuleTableRow } from "./Structure/RuleTableRow";
import { RuleTableColumn } from "./Structure/RuleTableRuleColumn";
import { RuleTableCellTestResult, RuleTableRowTestResult, RuleTableTestResult } from "./RuleTableTestResult";
import { IConfigStorage } from "../../../Common/IConfigStorage";
import { DataUtilities } from "../../../Common/DataUtilities";
import { IDesignValidationProvider, ValidationIssue } from "../../../Common/IDesignValidationProvider";
import { BaseRuleTableDefinition } from "../Common/BaseRuleTableDefinition";
import { TableColumn } from "../Common/TableColumn";
import { ResultCell } from "../Common/ResultCell";
import { ITestingContext } from "../../../Common/Testing/ITestingContext";
import { ExecutionData } from "../../../Common/Data/ExecutionData/ExecutionData";
import { EventLog, EventLogType } from "../../../Common/Event/EventLog";
import { IDataDefinition } from "../../../Common/Data/Definitions/IDataDefinition";
import { BooleanDataDefinition } from "../../../Common/Data/Definitions/BooleanDataDefinition";
import { DataTypes } from "../../../Common/Data/DataTypes";
import { ObjectDataDefinition } from "../../../Common/Data/Definitions/ObjectDataDefinition";
import { ExpressionConfiguration } from "../../../Expressions/ExpressionConfiguration";
import { log } from "../../../Log";
import { LogLevel } from "../../../Plugins/Logging/LogLevel";
import { ExpressionValueType } from "../../../Expressions/ExpressionValueType";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

// todo: render all result cols
// todo: make the + work
// todo: sync the outputs.  if they are supplied, we need to not have the + and the columns setup.  if not supplied, we need to populate the output
//   data def outputs on executable
// todo: is default editable in ui?
// todo: add result col is missing

export class RuleTableConfiguration
    extends BaseRuleTableDefinition
    implements IConfigStorage, IDesignValidationProvider
{
    _id: string = DataUtilities.generateUniqueId();
    title?: string;

    constructor() {
        super();
        if (this.canChangeOutputs) {
            this.addResultColumn();
        }
        this.addRow();
    }

    private _ruleColumns: RuleTableColumn[] = [new RuleTableColumn()];

    get ruleColumns(): RuleTableColumn[] {
        return this._ruleColumns;
    }

    private _rows: RuleTableRow[] = [];

    get rows(): RuleTableRow[] {
        return this._rows;
    }

    get defaultResult(): { [id: string]: ResultCell } {
        return this._defaultResult;
    }

    get resultColumns(): TableColumn[] {
        return this._resultColumns;
    }

    run(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny {
        this.syncAllRows();

        let subContext: RuleTableTestResult | undefined;

        if (testContext) {
            subContext = new RuleTableTestResult(this._id);
            subContext.definition = this;
            testContext.addTestResultItem(subContext);
            subContext.addLogItem(new EventLog(`RuleTable: start evaluating`));
        }

        for (let rowidx in this._rows) {
            let row = this._rows[rowidx];
            let evaluated = true;

            let rowContext: RuleTableRowTestResult | undefined;

            if (subContext) {
                rowContext = new RuleTableRowTestResult(row._id);
                rowContext.addLogItem(new EventLog(`RuleTable: start evaluating ${parseInt(rowidx, 10) + 1}th row`));
                subContext.addTestResultItem(rowContext);
            }

            for (let ruleColumn of this.ruleColumns) {
                let cell = row.ruleCells[ruleColumn._id];

                if (!cell.expression) {
                    throw new Error("Expression is not set.");
                }

                let ruleColumnContext: RuleTableCellTestResult | undefined;

                if (rowContext) {
                    ruleColumnContext = new RuleTableCellTestResult(`${row._id}-${cell._columnId}`);
                    ruleColumnContext.addLogItem(
                        new EventLog(`RuleTable: start evaluating "${ruleColumn.name || ruleColumn.path}" column`),
                    );
                    rowContext.addTestResultItem(ruleColumnContext);
                }
                try {
                    let res = cell.expression.run(data, ruleColumnContext);
                    evaluated = evaluated && res;

                    if (ruleColumnContext) {
                        ruleColumnContext.result = res.toString();
                        ruleColumnContext.addLogItem(
                            new EventLog(
                                `ValueTable: end evaluating "${ruleColumn.name || ruleColumn.path}" cell with result [${
                                    ruleColumnContext.result
                                }]`,
                            ),
                        );
                    }
                } catch (e) {
                    if (ruleColumnContext) {
                        ruleColumnContext.isError = true;
                        ruleColumnContext.result = e.message;
                        ruleColumnContext.addLogItem(new EventLog(e.message, EventLogType.ERROR));
                    }
                    if (rowContext) {
                        rowContext.isError = true;
                        rowContext.result = e.message;
                        rowContext.addLogItem(new EventLog(e.message, EventLogType.ERROR));
                    }
                    if (subContext) {
                        subContext.isError = true;
                        subContext.result = e.message;
                        subContext.addLogItem(new EventLog(e.message, EventLogType.ERROR));
                    }
                    throw e;
                }

                if (!evaluated) {
                    break;
                }
            }

            if (evaluated) {
                //  this is our result, return it.

                let resultdata = {};

                for (let rescol of this.resultColumns) {
                    Reflect.set(resultdata, rescol.name, row.resultCellsData[rescol._id].value);
                }

                if (rowContext) {
                    rowContext.result = "true";
                    rowContext.addLogItem(
                        new EventLog(
                            `RuleTable: end evaluating ${parseInt(rowidx, 10) + 1}th row with result [${evaluated}]`,
                        ),
                    );

                    if (subContext) {
                        subContext.addLogItems(rowContext.logs);
                        subContext.addLogItem(
                            new EventLog(
                                `RuleTable: end evaluating ${parseInt(rowidx, 10) + 1}th row with result [${evaluated}]`,
                            ),
                        );

                        subContext.result = JSON.stringify(resultdata);

                        subContext.addLogItem(
                            new EventLog(`RuleTable: end evaluating with result (${subContext.result})`),
                        );
                    }
                }

                return resultdata;
            } else if (rowContext) {
                rowContext.result = "false";
                rowContext.addLogItem(
                    new EventLog(
                        `RuleTable: end evaluating ${parseInt(rowidx, 10) + 1}th row with result [${evaluated}]`,
                    ),
                );

                if (subContext) {
                    subContext.addLogItems(rowContext.logs);
                    subContext.addLogItem(
                        new EventLog(
                            `RuleTable: end evaluating ${parseInt(rowidx, 10) + 1}th row with result [${evaluated}]`,
                        ),
                    );
                }
            }
        }

        if (this.allowDefaultResults) {
            let resultdata = {};

            for (let rescol of this.resultColumns) {
                Reflect.set(resultdata, rescol.name, this.defaultResult[rescol._id].value);
            }

            if (subContext) {
                subContext.result = JSON.stringify(resultdata);

                subContext.addLogItem(
                    new EventLog(`RuleTable: end evaluating with default result (${subContext.result})`),
                );
            }

            return resultdata;
        }
    }

    getDefaultResultByOrder(order: number): ResultCell | undefined {
        return this._defaultResult[this.resultColumns[order]?._id];
    }

    syncAllRows() {
        for (let r of this._rows) {
            this.syncRow(r);
        }
    }

    syncRow(row: RuleTableRow) {
        // todo: remove extra

        // remove the extra
        let columnsIds = this._ruleColumns.map((col) => col._id);
        for (let colId in row.ruleCells) {
            if (!columnsIds.includes(colId)) {
                delete row.ruleCells[colId];
            }
        }

        // verify that all the cells are present.
        for (let col of this._ruleColumns) {
            let cell = row.ruleCells[col._id];
            if (!cell) {
                cell = new RuleTableCell(col._id);
                row.ruleCells[col._id] = cell;
            } else {
                // ensure setup is correct
            }

            if (!cell.expression) {
                cell.expression = new ExpressionConfiguration();
                cell.expression.allowTrueValueResult = true;
                cell.expression.valueType = ExpressionValueType.SUBEXPRESSION;
            }

            if (!cell.expression.allowTrueValueResult) {
                cell.expression.allowTrueValueResult = true;
            }

            let datadef = this.getInputDefinition();
            if (datadef !== cell.expression.getInputDefinition()) {
                cell.expression.inputData = () => this.getInputDefinition();
            }

            if (cell.expression.getRuleResult().type !== DataTypes.BOOLEAN) {
                let bool = new BooleanDataDefinition();
                cell.expression.executionResult = () => {
                    return bool;
                };
            }

            if (!cell.expression.requiredVariable) {
                cell.expression.requiredVariable = this.getRuleColumnData(col);
            }
        }

        // remove the extra
        columnsIds = this._resultColumns.map((col) => col._id);
        for (let colId in row.resultCellsData) {
            if (!columnsIds.includes(colId)) {
                delete row.resultCellsData[colId];
            }
        }

        // verify that all the cells are present.
        for (let col of this._resultColumns) {
            let cell = row.resultCellsData[col._id];
            if (!cell) {
                cell = new ResultCell(col._id);
                row.resultCellsData[col._id] = cell;
            } else {
                // ensure setup is right
            }

            if ((cell.value === undefined || cell.value === null) && col.dataType === DataTypes.BOOLEAN) {
                cell.value = false;
            }

            if (!this.getExecutionResult().fields.find((f) => f.name === col.name && f.type === col.dataType)) {
                if (!this.canChangeOutputs) {
                    // remove bad result col;
                    this.removeResultColumn(col._id);
                } else {
                    // update the outputs with this col.
                    this.getExecutionResult().fields.push(DataUtilities.createDataByType(col.dataType!, col.name));
                }
            }
        }
    }

    addRow(row?: RuleTableRow): RuleTableRow {
        let newRow = row;
        if (!newRow) {
            newRow = new RuleTableRow();
        }
        this.syncRow(newRow);
        this._rows.push(newRow);

        return newRow;
    }

    removeRow(row: RuleTableRow) {
        this._rows = this._rows.filter((r) => r !== row);
    }

    // todo: this should just take in name
    addRuleColumn(name?: string): RuleTableColumn {
        let newCol = new RuleTableColumn();

        if (!name) {
            name = "";
            // no need to create the name as we can use the variable picker
            let index = this.ruleColumns.length;

            do {
                index = index + 1;
                name = "column_" + index;
            } while (this.getColumnByName(name) !== undefined);

            name = "column_" + index;
        }

        newCol.name = name;

        this._ruleColumns.push(newCol);

        this.syncAllRows();

        return newCol;
    }

    columnSafeDeletable(id: string): boolean {
        const ruleColumn = this.ruleColumns.find((col) => col._id === id);

        if (!ruleColumn) {
            throw new Error(`Can not find the rule column[${id}]`);
        }

        for (let row of this._rows) {
            const cell = row.ruleCells[ruleColumn._id];

            if (cell.expression && !cell.expression.isEmpty) {
                return false;
            }
        }
        return true;
    }

    removeRuleColumn(id: string) {
        if (this._ruleColumns.length > 1) {
            this._ruleColumns = this._ruleColumns.filter((c) => c._id !== id);
            this.syncAllRows();
        }
    }

    getResultColumnType(resultcol: TableColumn): IDataDefinition | undefined {
        return this.getExecutionResult().fields.find((f) => f.name === resultcol.name && f.type === resultcol.dataType);
    }

    setResultColumnType(rule: TableColumn, dataType: IDataDefinition) {
        if (!this.canChangeOutputs) throw new Error("it's not editable");

        let defaultResult = this.getDefaultResultByColumnId(rule._id);

        for (let row of this.rows) {
            if (dataType instanceof BooleanDataDefinition) {
                if (row.resultCellsData[rule._id].value === undefined || row.resultCellsData[rule._id].value === null) {
                    row.resultCellsData[rule._id].value = false;
                }
            }
            row.resultCellsData[rule._id].value = dataType.convert(row.resultCellsData[rule._id].value);
        }

        // not sure if it's needed
        if (defaultResult) {
            if (
                dataType instanceof BooleanDataDefinition &&
                (defaultResult.value === undefined || defaultResult.value === null)
            ) {
                defaultResult.value = false;
            }
            defaultResult.value = dataType.convert(defaultResult.value);
        }

        let outputdef = this.getExecutionResult();

        // remove old and add new;
        outputdef.fields = outputdef.fields.map((f) => {
            if (f.name === rule.name && f.type === rule.dataType) {
                return dataType;
            }
            return f;
        });

        rule.name = dataType.name;
        rule.dataType = dataType.type;
    }

    syncDefaultResults() {
        let columnsIds = this._resultColumns.map((col) => col._id);
        for (let colId in this._defaultResult) {
            if (!columnsIds.includes(colId)) {
                delete this._defaultResult[colId];
            }
        }

        let outputdef = this.getExecutionResult();

        // verify that all the cells are present.
        for (let col of this._resultColumns) {
            let cell = this._defaultResult[col._id];
            if (!cell) {
                this._defaultResult[col._id] = new ResultCell(col._id);
                cell = this._defaultResult[col._id];
            } else {
                // ensure setup is right
            }

            if ((cell.value === undefined || cell.value === null) && col.dataType === DataTypes.BOOLEAN) {
                cell.value = false;
            }

            if (col.name !== "") {
                try {
                    let field = DataUtilities.getFieldFromDataDefinitionByName(
                        outputdef.fields,
                        col.name,
                    ) as IDataDefinition;

                    if (field && field.type !== col.dataType) {
                        col.dataType = field.type;
                    }
                } catch (e) {}
            }
        }
    }

    removeResultColumn(id: string) {
        if (!this.canChangeOutputs) {
            throw new Error("cannot add result columns because the outputs are specified by API");
        }

        if (this._resultColumns.length > 1) {
            let resultColIdx = this._resultColumns.findIndex((resultCol) => resultCol._id === id);
            if (resultColIdx < 0) return;

            let resultcol = this._resultColumns[resultColIdx];
            this._resultColumns.splice(resultColIdx, 1);

            this.getExecutionResult().fields = this.getExecutionResult().fields.filter(
                (f) => f.name !== resultcol!.name || f.type !== resultcol!.dataType,
            );

            this.syncDefaultResults();

            this.syncAllRows();
        }
    }

    getResultColumnValidationIssues(rescol: TableColumn): ValidationIssue[] {
        let issues = rescol.getDesignValidationIssues();

        let outputdef = this.getExecutionResult();

        if (!rescol.dataType) {
            issues.push(new ValidationIssue("Datatype required"));
        }

        try {
            const field = DataUtilities.getFieldFromDataDefinitionByName(outputdef.fields, rescol.name);

            if (!field) issues.push(new ValidationIssue("Data not found"));
            else if (field && (field as IDataDefinition).type !== rescol.dataType) {
                issues.push(new ValidationIssue("Data type does not match required type"));
            } else if (field instanceof ObjectDataDefinition && field.fields.length === 0) {
                issues.push(new ValidationIssue("fields are not configured"));
            }
        } catch (e) {
            issues.push(new ValidationIssue("Data not found"));
        }

        return issues;
    }

    getRuleCellValidationIssues(rulecell: RuleTableCell): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        let column = this._ruleColumns.find((col) => col._id === rulecell._columnId);

        if (!column) {
            issues.push(new ValidationIssue("Rule column not found"));
        } else {
            issues.push(...rulecell.getDesignValidationIssues());
        }

        return issues;
    }

    getRuleColumnValidationIssues(rulecol: RuleTableColumn): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        issues.push(...rulecol.getDesignValidationIssues());

        let inputdef = this.getInputDefinition();

        if (rulecol.path) {
            try {
                const field = DataUtilities.getFieldFromDataDefinitionByName(inputdef.fields, rulecol.path);

                if (!field) issues.push(new ValidationIssue("Column data not found"));
            } catch (e) {
                // todo: why is there a catch here?  this is bad code.
                issues.push(new ValidationIssue("Error looking up column definition " + e));
            }
        }

        return issues;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        for (let colidx in this._ruleColumns) {
            const col = this._ruleColumns[colidx];
            issues.push(
                ...this.getRuleColumnValidationIssues(col).map((issue) => {
                    issue.issue = `Rule column [${col.name}]:: ${issue.issue}`;
                    return issue;
                }),
            );
        }
        for (let rescolidx in this._resultColumns) {
            const rescol = this._resultColumns[rescolidx];
            issues.push(
                ...this.getResultColumnValidationIssues(rescol).map((issue) => {
                    issue.issue = `Result column [${rescol.name}]:: ${issue.issue}`;
                    return issue;
                }),
            );

            if (this.allowDefaultResults) {
                issues.push(
                    ...this.getDefaultResultCellValidationIssues(this.defaultResult[rescol._id]).map((issue) => {
                        issue.issue = `Result default cell [${rescol.name}]:: ${issue.issue}`;
                        return issue;
                    }),
                );
            }
        }
        for (let rowidx in this.rows) {
            const row = this.rows[rowidx];
            for (let columnIdx in this.ruleColumns) {
                const column = this.ruleColumns[columnIdx];
                const cell = row.ruleCells[column._id];
                issues.push(
                    ...this.getRuleCellValidationIssues(cell).map((issue) => {
                        issue.issue = `Rule cell [${column.name}, ${parseInt(rowidx, 10) + 1}]:: ${issue.issue}`;
                        return issue;
                    }),
                );
            }

            for (let rescol of this.resultColumns) {
                let cell = row.resultCellsData[rescol._id];

                issues.push(
                    ...this.getResultCellValidationIssues(cell).map((issue) => {
                        issue.issue = `Result cell [${rescol.name}, ${parseInt(rowidx, 10) + 1}]:: ${issue.issue}`;
                        return issue;
                    }),
                );
            }
        }

        return issues;
    }

    getStorageData(): object {
        let data = super.getStorageData();

        let colData: object[] = [];

        for (let c of this.ruleColumns) {
            colData.push(c.getStorageData());
        }

        Reflect.set(data, "cols", colData);

        let resultData: object[] = [];

        for (let c of this.resultColumns) {
            resultData.push(c.getStorageData());
        }

        Reflect.set(data, "resultCols", resultData);

        let defaultsData: object = {};

        for (let cId in this.defaultResult) {
            Reflect.set(defaultsData, cId, this.defaultResult[cId].getStorageData());
        }

        Reflect.set(data, "defaults", defaultsData);

        let rowsData: object[] = [];

        for (let c of this.rows) {
            rowsData.push(c.getStorageData());
        }

        Reflect.set(data, "rows", rowsData);

        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        let colData: object[] = Reflect.get(data, "cols");

        this._ruleColumns = colData.map((value) => {
            let col = new RuleTableColumn();
            col.setStorageData(value);
            return col;
        });

        let resultData: object[] = Reflect.get(data, "resultCols");

        this._resultColumns = resultData.map((value) => {
            let col = new TableColumn();
            col.setStorageData(value);
            return col;
        });

        let defaultsData: object = Reflect.get(data, "defaults");

        for (let dd in defaultsData) {
            let resultCell = new ResultCell(dd);
            resultCell.setStorageData(Reflect.get(defaultsData, dd));
            this._defaultResult[resultCell._columnId] = resultCell;
        }

        let rowsData: object[] = Reflect.get(data, "rows");

        this._rows = rowsData.map((value) => {
            let col = new RuleTableRow();
            col.setStorageData(value);
            return col;
        });

        this.syncDefaultResults();
        this.syncAllRows();
    }

    getRuleColumnData(rule: RuleTableColumn): IDataDefinition | undefined {
        if (!rule.path) return undefined;

        try {
            const dataDef = this.getInputDefinition();

            const field = DataUtilities.getFieldFromDataDefinitionByName(dataDef.fields, rule.path) as IDataDefinition;
            return field;
        } catch (e) {
            return undefined;
        }
    }

    setRuleColumn(rule: RuleTableColumn, path: string) {
        const dataDef = this.getInputDefinition();

        try {
            const field = DataUtilities.getFieldFromDataDefinitionByName(dataDef.fields, path) as IDataDefinition;
            if (field) {
                rule.name = field.name;
                rule.path = path;
                for (let row of this.rows) {
                    if (!row.ruleCells[rule._id].expression) {
                        row.ruleCells[rule._id].expression = new ExpressionConfiguration();
                    }
                    row.ruleCells[rule._id].expression!.requiredVariable = field;
                }
            }
        } catch (e) {
            log("Rule Table", LogLevel.ERROR, "Set Rule Column Error:", e.message);
        }
    }

    getNextCell(cell: ResultCell): null | ResultCell {
        if (this.resultColumns.length === 0) return null;

        for (let rowIdx in this._rows) {
            let row = this._rows[rowIdx];

            if (row.resultCells.indexOf(cell) >= 0) {
                let resultcolIdx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
                if (resultcolIdx >= 0) {
                    if (this.resultColumns.length - 1 > resultcolIdx) {
                        return row.resultCellsData[this.resultColumns[resultcolIdx + 1]._id] || null;
                    } else {
                        if (parseInt(rowIdx, 10) < this._rows.length - 1) {
                            return this._rows[parseInt(rowIdx, 10) + 1].resultCellsData[this.resultColumns[0]._id];
                        }
                        return null;
                    }
                }
            }
        }
        if (Object.values(this.defaultResult).indexOf(cell) >= 0) {
            let idx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
            if (idx <= this.resultColumns.length - 1) {
                return this.defaultResult[this.resultColumns[idx + 1]._id];
            }
        }
        return null;
    }

    getPrevCell(cell: ResultCell): null | ResultCell {
        if (this.resultColumns.length === 0) return null;
        for (let rowIdx in this._rows) {
            let row = this._rows[rowIdx];
            if (row.resultCells.indexOf(cell) >= 0) {
                let resultcolIdx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
                if (resultcolIdx > 0) {
                    return row.resultCellsData[this.resultColumns[resultcolIdx - 1]._id] || null;
                } else {
                    if (parseInt(rowIdx, 10) > 0) {
                        return this._rows[parseInt(rowIdx, 10) - 1].resultCellsData[
                            this.resultColumns[this.resultColumns.length - 1]._id
                        ];
                    }
                    return null;
                }
            }
        }

        if (Object.values(this.defaultResult).indexOf(cell) >= 0) {
            let idx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
            if (idx > 0) {
                return this.defaultResult[this.resultColumns[idx - 1]._id];
            }
        }
        return null;
    }

    getNextRowCell(cell: ResultCell): null | ResultCell {
        for (let rowIdx in this._rows) {
            let row = this._rows[rowIdx];
            if (row.resultCells.indexOf(cell) >= 0) {
                let idx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
                if (parseInt(rowIdx, 10) < this._rows.length - 1) {
                    return this._rows[parseInt(rowIdx, 10) + 1].resultCellsData[this.resultColumns[idx]._id];
                } else if (parseInt(rowIdx, 10) === this._rows.length - 1 && this.allowDefaultResults) {
                    return this._defaultResult[this.resultColumns[idx]._id];
                } else {
                    return null;
                }
            }
        }

        return null;
    }

    getPrevRowCell(cell: ResultCell): null | ResultCell {
        for (let rowIdx in this._rows) {
            let row = this._rows[rowIdx];
            if (row.resultCells.indexOf(cell) >= 0) {
                let idx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
                if (parseInt(rowIdx, 10) > 0) {
                    return this._rows[parseInt(rowIdx, 10) - 1].resultCellsData[this.resultColumns[idx]._id];
                } else {
                    return null;
                }
            }
        }
        if (cell instanceof ResultCell) {
            if (Object.values(this.defaultResult).indexOf(cell) >= 0) {
                let idx = this.resultColumns.findIndex((rescol) => rescol._id === cell._columnId);
                return this._rows[this.rows.length - 1].resultCellsData[this.resultColumns[idx]._id];
            }
        }

        return null;
    }

    private getColumnByName(name: string): RuleTableColumn | undefined {
        return this.ruleColumns.find((c) => c.name === name);
    }
}
